/**
 * Giriş ekranı — ilk gerçek ekran.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * TOTP ALANI BAŞTAN GÖRÜNMEZ.
 *
 * TOTP herkes için OPSİYONELDİR [K-009]; kullanıcıların çoğunda kurulu
 * değil. Alanı baştan göstermek onlara "bir şey eksik mi?" hissi verir ve
 * boş bırakılması gereken bir alan sunar.
 *
 * Sunucu `totp_required` dönene kadar yalnız e-posta + parola vardır. Alan
 * belirdiğinde ODAK oraya gider — kullanıcı ne yapması gerektiğini aramaz.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Doğrulama `@gasco/shared`'ın `loginSchema`'sıyla — SUNUCUYLA AYNI TANIM
 * [K-020]. İstemcinin kabul edip sunucunun reddettiği bir giriş olamaz.
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { loginSchema, type LoginInput } from '@gasco/shared';

import { ApiError, istek } from '../lib/api.ts';
import { Button } from '../design/Button.tsx';
import { Field, Input } from '../design/Field.tsx';
import { useSessionRefresh } from './session.ts';

export function LoginPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { redirect, sona_erdi: sonaErdi } = useSearch({ from: '/giris' });
  const yenile = useSessionRefresh();
  const qc = useQueryClient();
  const [totpGerekli, setTotpGerekli] = useState(false);
  const [genelHata, setGenelHata] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setFocus,
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  // TOTP istendiğinde odağı oraya taşı — kullanıcı alanı aramasın.
  useEffect(() => {
    if (totpGerekli) setFocus('totpCode');
  }, [totpGerekli, setFocus]);

  const gonder = handleSubmit(async (degerler) => {
    setGenelHata(null);
    try {
      await istek('/auth/login', { method: 'POST', body: degerler });
      /*
       * ÖNBELLEK BURADA TEMİZLENİR — 401 yakalayıcısında değil (S37).
       *
       * Oturum düştüğünde önbelleği silmek sonsuz istek döngüsü üretir
       * (`auth/expiry.ts`); o yüzden eski kullanıcının verisi bir süre
       * önbellekte kalır. Giriş anı temizlik için doğru yer: ekranda sökülecek
       * bileşen ve tetiklenecek sorgu yok. Aynı zamanda "sona erdi" bayrağını
       * da siler.
       */
      qc.clear();
      // Oturum bilgisi giriş cevabından DEĞİL, `/auth/me`'den okunur.
      await yenile();
      // Kullanıcı istediği sayfaya döner — ana ekrana değil.
      await navigate({ to: redirect ?? '/sahalar' });
    } catch (error) {
      if (error instanceof ApiError && error.code === 'totp_required') {
        setTotpGerekli(true);
        // Bu bir hata değil, akışın bir adımı — kırmızı mesaj gösterme.
        return;
      }
      setGenelHata(error instanceof Error ? error.message : 'Giriş yapılamadı.');
    }
  });

  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface-sunken p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-ink">Gasco SCADA</h1>
          <p className="mt-1 text-sm text-ink-muted">Devam etmek için giriş yapın</p>
        </div>

        {sonaErdi === true && (
          /*
           * ─── HATA DEĞİL, AÇIKLAMA ────────────────────────────────────────
           * Kullanıcı bir işin ortasındayken buraya atıldı; ilk sorusu "ne
           * oldu?" olur. Kırmızı göstermiyoruz: yaptığı bir yanlış yok
           * (`totp_required` kararının aynısı — Görev 2). `role="status"`:
           * ekran okuyucu bunu, hata aciliyeti vermeden duyurur.
           */
          <p
            role="status"
            className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-3 text-sm text-ink-muted"
          >
            Oturumunuz sona erdi. Kaldığınız yerden devam etmek için tekrar giriş yapın.
          </p>
        )}

        <form
          onSubmit={(e) => void gonder(e)}
          noValidate
          className="flex flex-col gap-4 rounded-[var(--radius-card)] border border-border bg-surface p-5"
        >
          <Field label="E-posta" required error={errors.email?.message}>
            {({ id, required, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                required={required}
                type="email"
                // Mobilde doğru klavye ve otomatik doldurma.
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                {...register('email')}
              />
            )}
          </Field>

          <Field label="Parola" required error={errors.password?.message}>
            {({ id, required, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                required={required}
                type="password"
                autoComplete="current-password"
                {...register('password')}
              />
            )}
          </Field>

          {totpGerekli && (
            <Field
              label="Doğrulama kodu"
              required
              hint="Kimlik doğrulama uygulamanızdaki 6 haneli kod"
              error={errors.totpCode?.message}
            >
              {({ id, required, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  invalid={invalid}
                  required={required}
                  // Sayısal klavye + tek kullanımlık kod otomatik doldurma.
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={16}
                  {...register('totpCode')}
                />
              )}
            </Field>
          )}

          {genelHata !== null && (
            // `role="alert"`: sonradan beliren hata ekran okuyucuya duyurulur.
            <p role="alert" className="text-sm text-danger">
              {genelHata}
            </p>
          )}

          <Button type="submit" size="lg" block loading={isSubmitting}>
            {totpGerekli ? 'Doğrula ve giriş yap' : 'Giriş yap'}
          </Button>
        </form>
      </div>
    </div>
  );
}
